import { useEffect, useState } from "react"

import { FleetB2BPageShell } from "@/features/operator/fleet/fleet-b2b-page-shell"
import {
  applyFleetOnboardingRouteContext,
  FLEET_ONBOARDING_MODE_LABELS,
  getFleetKitTemplate,
  type FleetOnboardingSearch,
  resolveFleetOnboardingMode,
  resolveFleetOnboardingVertical,
  FLEET_VERTICAL_LABELS,
  useFleetOnboardingDraft,
} from "@/features/operator/fleet/fleet-onboarding-shared"
import {
  OperatorEmptyState,
  OperatorMetricCard,
  OperatorPanel,
  OperatorStatusMessage,
} from "@/features/operator/shared/operator-shell"
import {
  apiClient,
  isApiError,
  type FleetKitBatchRecord,
} from "@/lib/api/client"

interface FleetKitsPageProps {
  onOpenPrintPrep: (batchCode: string) => void
  onReturnToReview: () => void
  search: FleetOnboardingSearch
}

interface FleetIssueSummaryViewModel {
  eligibleAssets: number
  skippedAssets: number
  skippedReasons: Array<{
    count: number
    reason: string
    summary: string
  }>
  totalActiveAssets: number
  totalPreparedKits?: number
  totalPreparedStickers?: number
}

function toDraftIssuance(batch: FleetKitBatchRecord) {
  return {
    activationMode: batch.activationMode,
    batchCode: batch.batchCode,
    batchLabel: batch.batchLabel,
    kits: batch.kits,
    preparedAt: batch.preparedAt || batch.createdAt || null,
    templateKey: batch.templateKey as
      | "intercity_bus_passenger"
      | "trucking_standard"
      | "two_wheeler_delivery_shift",
    templateLabel: batch.templateLabel,
    totalAssets: batch.totalAssets,
    totalStickers: batch.totalStickers,
    vertical: batch.vertical,
  } as const
}

function normalizeIssueSummary(input: unknown): FleetIssueSummaryViewModel | null {
  if (!input || typeof input !== "object" || Array.isArray(input)) {
    return null
  }

  const source = input as Record<string, unknown>
  const skippedReasons = Array.isArray(source.skippedReasons)
    ? source.skippedReasons
        .filter((entry) => entry && typeof entry === "object" && !Array.isArray(entry))
        .map((entry) => {
          const row = entry as Record<string, unknown>
          return {
            count: Number(row.count) || 0,
            reason: typeof row.reason === "string" ? row.reason : "unknown",
            summary: typeof row.summary === "string" ? row.summary : "Asset was skipped during eligibility checks.",
          }
        })
    : []

  return {
    eligibleAssets: Number(source.eligibleAssets) || 0,
    skippedAssets: Number(source.skippedAssets) || 0,
    skippedReasons,
    totalActiveAssets: Number(source.totalActiveAssets) || 0,
    totalPreparedKits: Number.isFinite(Number(source.totalPreparedKits))
      ? Number(source.totalPreparedKits)
      : undefined,
    totalPreparedStickers: Number.isFinite(Number(source.totalPreparedStickers))
      ? Number(source.totalPreparedStickers)
      : undefined,
  }
}

function titleizeIssueReason(reason: string) {
  return reason
    .replace(/_/g, " ")
    .split(" ")
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(" ")
}

export function FleetKitsPage({
  onOpenPrintPrep,
  onReturnToReview,
  search,
}: FleetKitsPageProps) {
  const { draft, setDraft } = useFleetOnboardingDraft()
  const [issueError, setIssueError] = useState<string | null>(null)
  const [issueSummary, setIssueSummary] = useState<FleetIssueSummaryViewModel | null>(null)
  const [isHydratingRun, setIsHydratingRun] = useState(false)
  const [isHydratingBatch, setIsHydratingBatch] = useState(false)
  const [runHydrationError, setRunHydrationError] = useState<string | null>(null)
  const [isIssuingBatch, setIsIssuingBatch] = useState(false)
  const [latestRunBatchCode, setLatestRunBatchCode] = useState<string | null>(null)
  const [workflowStatus, setWorkflowStatus] = useState<string | null>(null)
  const selectedVertical = resolveFleetOnboardingVertical(search.vertical, draft)
  const selectedMode = resolveFleetOnboardingMode(search.mode, draft, search.step)
  const template = getFleetKitTemplate(selectedVertical)
  const issuanceDraft = draft.kitIssuance
  const persistedMaster = draft.masterPersistence

  const candidateAssetCount =
    persistedMaster?.assetCount || 0

  useEffect(() => {
    setDraft((previous) =>
      applyFleetOnboardingRouteContext(previous, {
        mode: search.mode,
        organizationId: search.organizationId,
        runId: search.runId,
        vertical: search.vertical,
      })
    )
  }, [search.mode, search.organizationId, search.runId, search.vertical, setDraft])

  const canPrepareBatch =
    Boolean(persistedMaster?.lastPersistedAt)
    && candidateAssetCount > 0
    && !isIssuingBatch

  useEffect(() => {
    const organizationId =
      search.organizationId
      ?? draft.activeOrganizationId
      ?? persistedMaster?.organizationId
      ?? null
    if (!organizationId) {
      return
    }

    let isActive = true
    setIsHydratingRun(true)
    setRunHydrationError(null)

    void apiClient
      .resolveFleetOnboardingRun({
        onboardingMode: selectedMode,
        organizationId,
        runId: search.runId ?? draft.activeRunId ?? persistedMaster?.runId ?? null,
        step: "review",
        vertical: selectedVertical,
      })
      .then((result) => {
        if (!isActive) {
          return
        }

        const runMasterPersistence = result.run.latestMasterPersistence
        const latestBatchCode = result.run.latestKitBatch?.batchCode || null
        setWorkflowStatus(result.run.workflowStatus || null)
        setLatestRunBatchCode(latestBatchCode)
        setDraft((previous) => ({
          ...applyFleetOnboardingRouteContext(previous, {
            mode: result.run.onboardingMode || undefined,
            organizationId: result.run.organizationId,
            runId: result.run.id,
            vertical: result.run.vertical,
          }),
          masterPersistence: runMasterPersistence
            ? {
                assetCount: runMasterPersistence.assetCount,
                assignmentCount: runMasterPersistence.assignmentCount,
                lastPersistedAt: runMasterPersistence.persistedAt || null,
                onboardingMode: runMasterPersistence.mode || selectedMode,
                organizationId: runMasterPersistence.organizationId || result.run.organizationId,
                peopleCount: runMasterPersistence.peopleCount,
                runId: runMasterPersistence.runId || result.run.id,
                unitCount: runMasterPersistence.unitCount,
                vertical: runMasterPersistence.vertical,
              }
            : previous.masterPersistence,
        }))
      })
      .catch((error) => {
        if (!isActive) {
          return
        }
        setRunHydrationError(
          error instanceof Error
            ? error.message
            : "Failed to hydrate onboarding run context for kit issuance."
        )
      })
      .finally(() => {
        if (isActive) {
          setIsHydratingRun(false)
        }
      })

    return () => {
      isActive = false
    }
  }, [
    draft.activeOrganizationId,
    draft.activeRunId,
    persistedMaster?.organizationId,
    persistedMaster?.runId,
    search.organizationId,
    search.runId,
    selectedMode,
    selectedVertical,
    setDraft,
  ])

  const resolvedBatchCode = search.batchCode || latestRunBatchCode

  useEffect(() => {
    if (!resolvedBatchCode) {
      return
    }

    if (issuanceDraft?.batchCode === resolvedBatchCode) {
      return
    }

    let isActive = true
    setIsHydratingBatch(true)
    setIssueError(null)

    void apiClient
      .getFleetKitBatch(resolvedBatchCode)
      .then((batch) => {
        if (!isActive) {
          return
        }

        setDraft((previous) => ({
          ...previous,
          organizationName: previous.organizationName || batch.organizationName || previous.organizationName,
          kitIssuance: toDraftIssuance(batch),
        }))
      })
      .catch((error) => {
        if (!isActive) {
          return
        }

        setIssueError(error instanceof Error ? error.message : "Failed to load the fleet kit batch.")
      })
      .finally(() => {
        if (isActive) {
          setIsHydratingBatch(false)
        }
      })

    return () => {
      isActive = false
    }
  }, [issuanceDraft?.batchCode, resolvedBatchCode, setDraft])

  async function prepareIssuanceBatch() {
    if (!canPrepareBatch) {
      return
    }

    setIsIssuingBatch(true)
    setIssueError(null)
    setIssueSummary(null)

    try {
      const resolvedRunId =
        search.runId
        ?? draft.activeRunId
        ?? persistedMaster?.runId
        ?? null

      const response = await apiClient.issueFleetKitBatch({
        onboardingMode: selectedMode,
        operatingRegions: draft.operatingRegions,
        organization: {
          city: draft.primaryCity,
          country: draft.primaryCountry,
          fleetAdminEmail: draft.adminEmail,
          fleetAdminName: draft.adminName,
          fleetAdminPhone: draft.adminPhone,
          fleetEnabled: true,
          fleetSize: candidateAssetCount,
          legalName: draft.legalName,
          name: draft.organizationName || "Fleet Organization",
          region: draft.operatingRegions,
          type: "fleet_partner",
        },
        organizationId: persistedMaster?.organizationId || search.organizationId || null,
        runId: resolvedRunId,
        vertical: selectedVertical,
      })

      setIssueSummary(normalizeIssueSummary(response.summary))
      setLatestRunBatchCode(response.batch.batchCode)
      setDraft((previous) => ({
        ...previous,
        organizationName: response.organization?.name || previous.organizationName,
        kitIssuance: toDraftIssuance(response.batch),
      }))
    } catch (error) {
      if (
        isApiError(error)
        && error.responseBody
        && typeof error.responseBody === "object"
        && !Array.isArray(error.responseBody)
      ) {
        const details = normalizeIssueSummary(
          (error.responseBody as Record<string, unknown>).details
        )
        if (details) {
          setIssueSummary(details)
        }
      }
      setIssueError(
        error instanceof Error ? error.message : "Failed to issue the fleet kit batch."
      )
    } finally {
      setIsIssuingBatch(false)
    }
  }

  function clearIssuanceBatch() {
    setDraft((previous) => ({
      ...previous,
      kitIssuance: null,
    }))
  }

  return (
    <FleetB2BPageShell
      activeSection="kits"
      title="Kits and issuance"
      description="Prepare one system-activated kit per asset, preview the vertical-specific sticker template, and batch the issue set before print/export."
      highlights={[
        "Kits replace the old flat tag-generation mental model for B2B issuance.",
        "One truck, bus, or bike gets one kit with multiple slot-specific stickers.",
        "System activation happens at issuance time instead of public self-registration.",
      ]}
    >
      <div className="grid gap-5">
        <OperatorStatusMessage>
          Issuance vertical: <strong>{FLEET_VERTICAL_LABELS[selectedVertical]}</strong>. Source mode:
          <strong> {FLEET_ONBOARDING_MODE_LABELS[selectedMode]}</strong>.
        </OperatorStatusMessage>

        {persistedMaster?.lastPersistedAt ? (
          <OperatorStatusMessage>
            Fleet kits are now issuing from persisted backend masters only. Latest persistence:
            <strong> {new Date(persistedMaster.lastPersistedAt).toLocaleString()}</strong>.
          </OperatorStatusMessage>
        ) : (
          <OperatorStatusMessage tone="warning">
            No persisted fleet masters are loaded for this onboarding draft yet. Return to review and persist the roster before issuing kits.
          </OperatorStatusMessage>
        )}

        {isHydratingRun ? (
          <OperatorStatusMessage>
            Loading authoritative onboarding run context for kits and continuity.
          </OperatorStatusMessage>
        ) : null}

        {runHydrationError ? (
          <OperatorStatusMessage tone="danger">{runHydrationError}</OperatorStatusMessage>
        ) : null}

        {isHydratingBatch ? (
          <OperatorStatusMessage>
            Loading persisted fleet kit batch <strong>{resolvedBatchCode}</strong>.
          </OperatorStatusMessage>
        ) : null}

        {issueError ? (
          <OperatorStatusMessage tone="danger">{issueError}</OperatorStatusMessage>
        ) : null}

        <section className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(0,0.95fr)]">
          <OperatorPanel
            title="Issuance template"
            description="This is the default system-activated kit template for the selected fleet vertical."
          >
            <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                Active kit template
              </div>
              <div className="mt-2 text-2xl font-semibold">{template.label}</div>
              <div className="mt-3 grid gap-3 md:grid-cols-2">
                {template.slots.map((slot) => (
                  <div
                    key={slot.slotKey}
                    className="rounded-2xl border border-border/70 bg-background px-3 py-3"
                  >
                    <div className="text-sm font-semibold">{slot.label}</div>
                    <div className="mt-1 text-sm text-muted-foreground">
                      {slot.visibility} • {slot.scanFlow}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </OperatorPanel>

          <OperatorPanel
            title="Batch readiness"
            description="Issue one backend-owned batch that persists system-activated stickers and becomes the source of truth for print/export."
          >
            <div className="grid gap-3 md:grid-cols-2">
              <OperatorMetricCard
                label="Candidate assets"
                value={candidateAssetCount}
                detail="Persisted assets currently eligible to receive a system-issued kit."
              />
              <OperatorMetricCard
                label="Sticker slots per asset"
                value={template.slots.length}
                detail="Each asset gets one sticker per slot in the selected template."
              />
              <OperatorMetricCard
                label="Prepared assets"
                value={issuanceDraft?.totalAssets || 0}
                detail="Assets currently included in the prepared issuance draft."
              />
              <OperatorMetricCard
                label="Prepared stickers"
                value={issuanceDraft?.totalStickers || 0}
                detail="Total sticker count in the current issuance draft."
              />
              <OperatorMetricCard
                label="Skipped assets"
                value={issueSummary?.skippedAssets || 0}
                detail="Active assets excluded by eligibility checks during the latest issuance attempt."
              />
              <OperatorMetricCard
                label="Eligible assets"
                value={issueSummary?.eligibleAssets || 0}
                detail="Assets confirmed as issuable in the latest backend summary."
              />
            </div>

            <div className="mt-4 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => {
                  void prepareIssuanceBatch()
                }}
                disabled={!canPrepareBatch}
                className="rounded-2xl bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isIssuingBatch ? "Issuing kits..." : "Issue fleet kits"}
              </button>
              <button
                type="button"
                onClick={clearIssuanceBatch}
                className="rounded-2xl border border-border px-4 py-2 text-sm font-medium"
              >
                Clear batch draft
              </button>
              <button
                type="button"
                onClick={onReturnToReview}
                className="rounded-2xl border border-border px-4 py-2 text-sm font-medium"
              >
                Return to review
              </button>
            </div>

            {!canPrepareBatch ? (
              <OperatorStatusMessage tone="warning">
                No persisted review-ready onboarding assets are available yet. Finish onboarding review and persist the fleet masters before preparing the kit batch.
              </OperatorStatusMessage>
            ) : (
              <OperatorStatusMessage>
                Kit issuance now persists through the Fleet backend. Each generated sticker code is stored as a real fleet tag and grouped into a retrievable batch record.
              </OperatorStatusMessage>
            )}

            {issueSummary?.skippedReasons.length ? (
              <div className="mt-4 grid gap-2">
                {issueSummary.skippedReasons.map((row) => (
                  <div
                    key={`${row.reason}-${row.count}`}
                    className="rounded-2xl border border-border/70 bg-background/65 px-3 py-2 text-sm text-muted-foreground"
                  >
                    <strong>{row.count}</strong> • <strong>{titleizeIssueReason(row.reason)}</strong> • {row.summary}
                  </div>
                ))}
              </div>
            ) : null}
          </OperatorPanel>
        </section>

        <OperatorPanel
          title="Post-issuance continuity"
          description="Confirm completion state for this onboarding run and reopen the latest prepared batch at any time."
        >
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            <OperatorMetricCard
              label="Workflow status"
              value={workflowStatus || "onboarding_in_progress"}
              detail="Derived from the authoritative onboarding run metadata."
            />
            <OperatorMetricCard
              label="Latest run batch"
              value={latestRunBatchCode || "Not prepared"}
              detail="Persistent reference used to reopen print/export."
            />
            <OperatorMetricCard
              label="Eligible in last run"
              value={issueSummary?.eligibleAssets ?? issuanceDraft?.totalAssets ?? 0}
              detail="Most recent issuance eligibility snapshot for this run."
            />
            <OperatorMetricCard
              label="Prepared stickers"
              value={issueSummary?.totalPreparedStickers ?? issuanceDraft?.totalStickers ?? 0}
              detail="Sticker volume prepared in the latest issued batch."
            />
          </div>
          <div className="mt-4 flex flex-wrap gap-3">
            <button
              type="button"
              disabled={!latestRunBatchCode}
              onClick={() => {
                if (latestRunBatchCode) {
                  onOpenPrintPrep(latestRunBatchCode)
                }
              }}
              className="rounded-2xl border border-border px-4 py-2 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50"
            >
              Reopen latest print/export batch
            </button>
          </div>
        </OperatorPanel>

        <OperatorPanel
          title="Prepared issuance batch"
          description="Review the kit batch before moving into print/export preparation."
        >
          {!issuanceDraft ? (
            <OperatorEmptyState
              title="No issuance batch prepared yet."
              message="Prepare the issuance batch to convert onboarding-ready assets into kit and sticker previews."
            />
          ) : (
            <div className="grid gap-4">
              <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                  Batch
                </div>
                <div className="mt-2 text-2xl font-semibold">{issuanceDraft.batchLabel}</div>
                <div className="mt-2 text-sm leading-6 text-muted-foreground">
                  {issuanceDraft.batchCode} • activation mode <strong>{issuanceDraft.activationMode}</strong> • prepared{" "}
                  <strong>{issuanceDraft.preparedAt ? new Date(issuanceDraft.preparedAt).toLocaleString() : "now"}</strong>
                </div>
              </div>

              <div className="grid gap-3">
                {issuanceDraft.kits.slice(0, 6).map((kit) => (
                  <div
                    key={`${kit.assetNumber}-${kit.operatingUnitLabel}`}
                    className="rounded-3xl border border-border/70 bg-background/70 px-4 py-4"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <div className="text-sm font-semibold">{kit.assetNumber}</div>
                        <div className="mt-1 text-sm text-muted-foreground">
                          {kit.operatingUnitLabel} • {kit.operatorLabel}
                        </div>
                        <div className="mt-1 text-sm text-muted-foreground">{kit.routeOrZone}</div>
                      </div>
                      <div className="rounded-full border border-border/70 px-3 py-1 text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">
                        {kit.slots.length} stickers
                      </div>
                    </div>

                    <div className="mt-4 grid gap-2 md:grid-cols-2 xl:grid-cols-4">
                      {kit.slots.map((slot) => (
                        <div
                          key={`${kit.assetNumber}-${slot.slotKey}`}
                          className="rounded-2xl border border-border/60 bg-background px-3 py-3"
                        >
                          <div className="text-sm font-medium">{slot.label}</div>
                          <div className="mt-1 text-xs text-muted-foreground">
                            {slot.visibility} • {slot.scanFlow}
                          </div>
                          <div className="mt-2 font-mono text-xs">{slot.previewCode}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => onOpenPrintPrep(issuanceDraft.batchCode)}
                  className="rounded-2xl bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
                >
                  Open print/export prep
                </button>
              </div>
            </div>
          )}
        </OperatorPanel>
      </div>
    </FleetB2BPageShell>
  )
}
